import { useEffect, useState } from 'react';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

export type Reducer<S, A> = (state: S, action: A) => S;
export type Dispatch<A> = (action: A) => void;

/**
 * This utility interface should NOT be used directly as a type for any Action
 * It is useful for extracting specific Action types from a Union of Action types
 * See `ofType` below for a usage example
 */
export interface GenericAction<T extends string = string> {
  type: T;
}

/**
 * Pipable operator that filters an Observable of Actions to those of the specified type
 */
export function ofType<A extends GenericAction, T extends A['type']>(type: T) {
  type FilteredAction = Extract<A, GenericAction<T>>;
  return filter<A, FilteredAction>((action): action is FilteredAction => {
    return action.type === type;
  });
}

/**
 * Similar to `fromEvent` in rxjs
 * Use this when you want to treat an Action like an Event for side effects
 */
export function fromAction<A extends GenericAction<string>, T extends A['type']>(actionStream: Subject<A>, actionType: T) {
  return actionStream.pipe(ofType(actionType));
}

/**
 * StoreSubject is a BehaviorSubject that obfuscates the "value" accessor
 */
export class StoreSubject<S> extends BehaviorSubject<S> {
  public imperativelyGetValue = () => {
    return super.getValue();
  };
  public getValue = () => {
    throw new Error(
      "You're trying to read the value of a StoreSubject directly. If you " +
        "want to access this store's state, pipe and/or subscribe to it. If " +
        'you really need to to access the state from imperative code, use the' +
        '`.imperativelyGetValue()` method.'
    );
  };
}

/**
 * Helper to create a StoreSubject and wire it up to a stream of Actions via a Reducer
 */
export function createStore<S, A>(reducer: Reducer<S, A>, initialState: S, actionStream: Subject<A>): StoreSubject<S> {
  const stateStream = new StoreSubject<S>(initialState);
  actionStream.subscribe((action) => {
    const prevState = stateStream.imperativelyGetValue();
    const nextState = reducer(prevState, action);
    stateStream.next(nextState);
  });
  return stateStream;
}

/**
 * Helper to create a stream of Actions along with a Dispatch function
 */
export function createActionStream<A>() {
  const actionStream = new Subject<A>();
  const dispatch: Dispatch<A> = (action) => {
    actionStream.next(action);
  };
  return [actionStream, dispatch] as const;
}

/**
 * React Hook to wire up a StoreSubject for use in a React.FunctionComponent
 */
export function useStore<S>(store: StoreSubject<S>) {
  const [state, setState] = useState(store.imperativelyGetValue());

  useEffect(() => {
    const subscription = store.subscribe((s) => setState(s));
    return () => subscription.unsubscribe();
  }, [store]);

  return state;
}
